/**
 * Wrappers tipados para /api/reportes (§7 SRS).
 */

import { apiClient } from './apiClient'
import type { Client } from './clientesClient'

export type ReportStatus = 'draft' | 'pdf_generated' | 'closed'

export type ReportListItem = {
  id: number
  folio: string
  client_id: number | null
  client_alias: string | null
  work_order: string | null
  status: ReportStatus
  start_date: string | null
  end_date: string | null
  created_at: string
  closed_at: string | null
  created_by: number | null
  created_by_name: string | null
}

export type Report = {
  id: number
  tenant_id: number
  folio: string
  client_id: number | null
  work_order: string | null
  system: string | null
  building: string | null
  city: string | null
  start_date: string | null
  end_date: string | null
  activities: string[]
  technician_description: string | null
  technician_notes: string | null
  enhanced_description: string | null
  enhanced_notes: string | null
  status: ReportStatus
  service_responsible_name: string | null
  service_responsible_company: string | null
  client_responsible_name: string | null
  client_responsible_company: string | null
  created_at: string
  pdf_generated_at: string | null
  client_signature_path: string | null
  closed_at: string | null
  closed_by: number | null
  created_by: number | null
  created_by_name: string | null
}

export type ReportImage = {
  id: number
  report_id: number
  addendum_id: number | null
  path: string
  technician_description: string
  enhanced_description: string | null
  display_order: number
  created_at: string
}

export type Addendum = {
  id: number
  report_id: number
  sequence_number: number
  reason: string | null
  content: string
  integrated_at: string | null
  author_user_id: number
  author_name: string | null
  author_email: string | null
  created_at: string
}

export type ReportChange = {
  id: number
  report_id: number
  addendum_id: number | null
  change_type: 'addendum' | string
  change_description: string
  author_user_id: number
  author_name_snapshot: string
  author_email_snapshot: string
  occurred_at: string
}

export type ReportDetail = {
  report: Report
  client: Client | null
  images: ReportImage[]
  addendums: Addendum[]
  changes: ReportChange[]
}

export type AddendumPayload = {
  content: string
}

export type ReportListResponse = {
  items: ReportListItem[]
  next_cursor: number | null
  total: number
}

export type ReportPayload = Partial<{
  client_id: number | null
  work_order: string
  system: string
  building: string
  city: string
  start_date: string
  end_date: string
  activities: string[]
  technician_description: string
  technician_notes: string
  enhanced_description: string | null
  enhanced_notes: string | null
  service_responsible_name: string
  service_responsible_company: string
  client_responsible_name: string
  client_responsible_company: string
}>

export type ActivityOption = { value: string; label: string }

export type ReportListFilters = {
  q?: string
  date_from?: string
  date_to?: string
  status?: ReportStatus | ''
}

export const reportesClient = {
  list: (filters: ReportListFilters = {}, cursor?: number | null, limit = 25) => {
    const qs = new URLSearchParams()
    if (cursor != null) qs.set('cursor', String(cursor))
    qs.set('limit', String(limit))
    if (filters.q) qs.set('q', filters.q)
    if (filters.date_from) qs.set('date_from', filters.date_from)
    if (filters.date_to) qs.set('date_to', filters.date_to)
    if (filters.status) qs.set('status', filters.status)
    return apiClient.get<ReportListResponse>(`/reportes?${qs.toString()}`)
  },

  show: (id: number) => apiClient.get<ReportDetail>(`/reportes/${id}`),

  create: (payload: ReportPayload) =>
    apiClient.post<{ report: Report }>('/reportes', payload),

  update: (id: number, payload: ReportPayload) =>
    apiClient.patch<{ report: Report }>(`/reportes/${id}`, payload),

  remove: (id: number) => apiClient.delete<void>(`/reportes/${id}`),

  uploadImage: (reportId: number, file: File, description: string) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('description', description)
    return apiClient.post<{ image: ReportImage }>(`/reportes/${reportId}/imagenes`, fd)
  },

  updateImageDescription: (reportId: number, imageId: number, description: string) =>
    apiClient.patch<{ image: ReportImage }>(
      `/reportes/${reportId}/imagenes/${imageId}`,
      { description },
    ),

  deleteImage: (reportId: number, imageId: number) =>
    apiClient.delete<void>(`/reportes/${reportId}/imagenes/${imageId}`),

  reorderImages: (reportId: number, items: { id: number; display_order: number }[]) =>
    apiClient.patch<{ images: ReportImage[] }>(`/reportes/${reportId}/imagenes/reorder`, { items }),

  updateEnhancedImageDescription: (reportId: number, imageId: number, description: string) =>
    apiClient.patch<{ image: ReportImage }>(
      `/reportes/${reportId}/imagenes/${imageId}`,
      { enhanced_description: description },
    ),

  markPdfGenerated: (id: number) =>
    apiClient.post<{ report: Report }>(`/reportes/${id}/mark-pdf-generated`),

  close: (id: number, clientSignatureBase64?: string) =>
    apiClient.post<{ report: Report }>(`/reportes/${id}/close`,
      clientSignatureBase64 ? { client_signature_base64: clientSignatureBase64 } : {},
    ),

  sendReportPdf: (id: number, pdfBase64: string) =>
    apiClient.post<{ sent: boolean; to: string }>(`/reportes/${id}/send-report-pdf`, {
      pdf_base64: pdfBase64,
    }),

  /**
   * Paso 4 del flujo (§7.3.5 SRS) — "Mejorar redacción".
   * Internamente usa Claude API; el copy/errores son neutrales (AI-07).
   * Devuelve el reporte actualizado + imágenes con descripciones enhanced + contador de usos.
   */
  improveWriting: (id: number) =>
    apiClient.post<{
      report: Report
      images: ReportImage[]
      usage_this_month: number
      monthly_limit: number
    }>(`/reportes/${id}/improve-writing`),

  /** Catálogo de actividades válidas (público). */
  actividades: () =>
    apiClient.get<{ items: ActivityOption[] }>('/catalogos/actividades', { skipAuth: true }),

  // ─── Complementos (§7.4 SRS — rediseño) ───────────────────────────────
  addendums: {
    /** Lista complementos + log de cambios del reporte. */
    list: (reportId: number) =>
      apiClient.get<{ addendums: Addendum[]; changes: ReportChange[] }>(
        `/reportes/${reportId}/addendums`,
      ),

    /** Crea un complemento en borrador (integrated_at = null). */
    create: (reportId: number, payload: AddendumPayload) =>
      apiClient.post<{ addendum: Addendum }>(`/reportes/${reportId}/addendums`, payload),

    /** Edita un complemento en borrador. */
    update: (reportId: number, addendumId: number, content: string) =>
      apiClient.patch<{ addendum: Addendum }>(`/reportes/${reportId}/addendums/${addendumId}`, { content }),

    /** Elimina un complemento en borrador. */
    remove: (reportId: number, addendumId: number) =>
      apiClient.delete<void>(`/reportes/${reportId}/addendums/${addendumId}`),

    /** Integra todos los borradores pendientes al reporte cerrado. */
    integrate: (reportId: number) =>
      apiClient.post<{ integrated: number; addendums: Addendum[] }>(`/reportes/${reportId}/addendums/integrate`),

    /** Sube una imagen asociada al complemento. */
    uploadImage: (reportId: number, addendumId: number, file: File, description: string) => {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('description', description)
      return apiClient.post<{ image: ReportImage }>(
        `/reportes/${reportId}/addendums/${addendumId}/imagenes`,
        fd,
      )
    },

    /** Borra una imagen del addendum. */
    deleteImage: (reportId: number, addendumId: number, imageId: number) =>
      apiClient.delete<void>(
        `/reportes/${reportId}/addendums/${addendumId}/imagenes/${imageId}`,
      ),
  },
}

export const STATUS_LABELS: Record<ReportStatus, string> = {
  draft: 'Borrador',
  pdf_generated: 'PDF generado',
  closed: 'Cerrado',
}
